import './FetchFilterBar.scss';
import { Category, FilterPaneAdvancedState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  filterTokenizer,
  makeSuggestionGenerator
} from '@blisspointmedia/bpm-types/dist/advancedFilterParser';
import { Form } from 'react-bootstrap';
import * as R from 'ramda';
import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import {
  guessColFromUnknownName,
  isInvalidAnalyticsDimensionFromPrettyName
} from '@blisspointmedia/bpm-types/dist/Columns';
import { useExperimentalAnalyticsCols } from '../../utils/hooks/useExperimentalAnalyticsCols';
import {
  getInvalidGranularityDetailsBySource,
  InvalidGranularityDetails
} from '../../utils/columnMetricSystem/configUtils';
import { SingleChannelMediaTypes } from '../../utils/performance-utils';
import { Button } from '../Button/Button';
import { Design } from '../designConstants';

interface FilterPaneAdvancedProps {
  categories: Category[];
  compileError: string;
  readonly?: boolean;
  setEditing: Dispatch<SetStateAction<boolean>>;
  setState: Dispatch<SetStateAction<FilterPaneAdvancedState>>;
  state: string;
  showAdvancedHeader?: boolean;
  analyticsIsSource?: boolean;
  // current metrics table dims + data cols; filter dims are checked together with them
  tableCols?: string[];
  incompatibleFilterDimsFromDelivery?: InvalidGranularityDetails;
  channel?: SingleChannelMediaTypes;
}

const EMPTY_COLS: string[] = [];

const suggestionHelpMap = {
  '=': '= (equal to)',
  '!=': '!= (not equal to)',
  '~': '~ (like)',
  '!~': '!~ (not like)'
};

export const FilterPaneAdvanced = ({
  categories,
  compileError,
  readonly,
  setEditing,
  setState,
  state,
  showAdvancedHeader,
  analyticsIsSource = false,
  tableCols = EMPTY_COLS,
  incompatibleFilterDimsFromDelivery = {},
  channel
}: FilterPaneAdvancedProps) => {
  const advancedText = state;
  const setAdvancedTextRaw = useCallback((text: string) => setState(text), [setState]);
  const expAnalyticsCols = useExperimentalAnalyticsCols();

  const [cursorPosition, setCursorPosition] = useState(0);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const updateCursorPosition = useCallback(() => {
    const pos = textAreaRef.current?.selectionStart;
    if (pos !== undefined) {
      setCursorPosition(pos);
    }
  }, []);

  const setAdvancedText = useCallback(
    (text: string) => {
      setAdvancedTextRaw(text);
      updateCursorPosition();
    },
    [updateCursorPosition, setAdvancedTextRaw]
  );

  const forceCursorPosition = useCallback((pos: number) => {
    if (textAreaRef.current) {
      textAreaRef.current.setSelectionRange(pos, pos);
      setCursorPosition(pos);
    }
  }, []);

  const suggestionGenerator = useMemo(() => makeSuggestionGenerator(categories), [categories]);

  const suggestionObject = useMemo(() => {
    const tokens = filterTokenizer(advancedText);
    const result = suggestionGenerator(advancedText, tokens, cursorPosition);
    const activeDims: string[] = [];
    tokens.forEach(token => {
      if (token.type === 'cat') {
        const dimName = guessColFromUnknownName(token.value);
        if (dimName) {
          activeDims.push(dimName);
        }
      }
    });

    if (analyticsIsSource) {
      result.suggestions = result.suggestions.filter(
        item => !isInvalidAnalyticsDimensionFromPrettyName(item, expAnalyticsCols)
      );
    }
    const invalidGranularityDetails = getInvalidGranularityDetailsBySource(
      activeDims,
      tableCols,
      categories.map(cat => cat.key || ''),
      channel || ''
    );
    result.suggestions = result.suggestions.filter(item => {
      const colNameGuess = guessColFromUnknownName(item);
      if (colNameGuess) {
        return (
          !invalidGranularityDetails.table[colNameGuess] &&
          !incompatibleFilterDimsFromDelivery[colNameGuess] &&
          !invalidGranularityDetails.filter[colNameGuess]
        );
      }
      return true;
    });
    return result;
  }, [
    advancedText,
    suggestionGenerator,
    cursorPosition,
    analyticsIsSource,
    expAnalyticsCols,
    tableCols,
    incompatibleFilterDimsFromDelivery,
    categories,
    channel
  ]);

  const selectingLikeValue = useMemo(() => {
    const tokens = filterTokenizer(advancedText);
    const lastCompleteToken = tokens[tokens.length - 2];
    const isLikeComp =
      lastCompleteToken?.type === 'comp' && ['~', '!~'].includes(lastCompleteToken?.value);

    if (!isLikeComp || R.isNil(lastCompleteToken?.end)) {
      return false;
    }

    const textAfterComp = advancedText.slice(lastCompleteToken.end, cursorPosition).trim();

    return textAfterComp.length > 0;
  }, [advancedText, cursorPosition]);

  const defaultSuggestions = suggestionObject.suggestions;

  const logicalSuggestions = ['and', 'or'];
  const showLogicalSuggestions =
    selectingLikeValue && logicalSuggestions.some(s => !defaultSuggestions.includes(s));

  const insertText = useCallback(
    (text: string) => {
      const [newText, newPos] = suggestionObject.inserter(text);
      setAdvancedText(newText);
      forceCursorPosition(newPos);
    },
    [forceCursorPosition, suggestionObject, setAdvancedText]
  );

  const appendAtEnd = useCallback(
    (textToAppend: string) => {
      const isCursorAtEnd = cursorPosition === advancedText.length;
      if (isCursorAtEnd) {
        const newText = `${advancedText} ${textToAppend} `;
        setAdvancedText(newText);
        forceCursorPosition(newText.length);
      } else {
        insertText(textToAppend);
      }
    },
    [advancedText, cursorPosition, forceCursorPosition, insertText, setAdvancedText]
  );

  return (
    <div className='filter-pane-body'>
      {showAdvancedHeader !== false && (
        <div className='filter-pane-advanced-header'>Advanced Filter</div>
      )}
      <div className='filter-pane-advanced-box-container'>
        <Form.Control
          disabled={readonly}
          spellCheck={false}
          ref={textAreaRef}
          className='filter-pane-advanced-box'
          as='textarea'
          rows={6}
          value={advancedText}
          onKeyUp={updateCursorPosition}
          onClick={updateCursorPosition}
          onChange={e => setAdvancedText(e.currentTarget.value)}
          onFocus={() => setEditing(true)}
          onBlur={() => setEditing(false)}
        />
      </div>
      {compileError && <div className='compile-error'>{compileError}</div>}
      {showLogicalSuggestions && (
        <div className='suggestion-list'>
          {logicalSuggestions
            .filter(s => !defaultSuggestions.includes(s))
            .map(suggestion => (
              <Button
                key={suggestion}
                className='suggestion'
                design={Design.NEUTRAL}
                onClick={() => appendAtEnd(suggestion)}
              >
                {suggestionHelpMap[suggestion] || suggestion || '""'}
              </Button>
            ))}
        </div>
      )}
      {!readonly && (
        <div className='suggestion-list'>
          {suggestionObject.suggestions.map(suggestion => (
            <Button
              key={suggestion}
              className='suggestion'
              design={Design.NEUTRAL}
              onClick={() => insertText(suggestion)}
            >
              {suggestionHelpMap[suggestion] || suggestion || '""'}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
